import BaseTable from 'components/Tables/BaseTable';
import { usePersonnel } from 'features/Responder/features/Personnel';
import { isRunOpen, resolveRunScheduleId } from 'features/Responder/features/Schedules/utils';
import React, { useMemo } from 'react';
import { useSchedules } from 'features/Responder/features/Schedules/contexts/SchedulesContext';

export const RunHistory = ({
  scheduleId = null,
  scheduleIds = null,
  showScheduleColumn = false,
  enableRunSelection = false,
  statusFilter = 'all',
  useParentScroll = false,
}) => {
  const { runs, schedules, setActiveEditSchedule, setActiveRunId } = useSchedules();
  const { personnel } = usePersonnel();

  const scopedScheduleIds = useMemo(() => {
    if (Array.isArray(scheduleIds) && scheduleIds.length > 0) {
      return Array.from(
        new Set(
          scheduleIds.filter((value) => value != null && value !== '').map((value) => `${value}`),
        ),
      );
    }
    if (scheduleId != null && scheduleId !== '') return [`${scheduleId}`];
    return [];
  }, [scheduleId, scheduleIds]);

  const scheduleLookup = useMemo(() => {
    const map = new Map();
    if (!Array.isArray(schedules)) return map;
    schedules.forEach((schedule) => {
      if (schedule?.id == null) return;
      map.set(`${schedule.id}`, schedule);
    });
    return map;
  }, [schedules]);

  const shouldShowScheduleColumn = useMemo(
    () =>
      showScheduleColumn ||
      (!scheduleId && !scopedScheduleIds.length) ||
      scopedScheduleIds.length > 1,
    [scheduleId, scopedScheduleIds.length, showScheduleColumn],
  );

  // Define columns for BaseTable
  const columns = useMemo(
    () => [
      ...(shouldShowScheduleColumn
        ? [
            {
              header: 'Schedule',
              accessorKey: 'scheduleId',
              Cell: ({ cell }) => {
                const runScheduleId = cell.getValue();
                const found = scheduleLookup.get(`${runScheduleId}`);
                return (
                  <span className="text-white fs-6">{found?.name || runScheduleId || '-'}</span>
                );
              },
            },
          ]
        : []),
      {
        header: 'Started By',
        accessorKey: 'startedBy',
        Cell: ({ cell, row }) => {
          const userId = cell.getValue() || row.original.user_id;
          const name =
            (userId &&
              personnel &&
              personnel[userId] &&
              (personnel[userId].name || personnel[userId].fullName)) ||
            userId ||
            '';
          return <span className="text-white fs-6">{name}</span>;
        },
      },
      {
        header: 'Status',
        accessorKey: 'status',
        Cell: ({ cell }) => <span className="text-white fs-6">{cell.getValue()}</span>,
      },
      {
        header: 'Started At',
        accessorKey: 'started_at',
        Cell: ({ cell }) => (
          <span className="text-white fs-6">
            {cell.getValue() ? new Date(cell.getValue()).toLocaleString() : ''}
          </span>
        ),
      },
      {
        header: 'Ended At',
        accessorKey: 'ended_at',
        Cell: ({ cell }) => (
          <span className="text-white fs-6">
            {cell.getValue() ? new Date(cell.getValue()).toLocaleString() : ''}
          </span>
        ),
      },
    ],
    [personnel, scheduleLookup, shouldShowScheduleColumn],
  );

  const rows = useMemo(() => {
    const source = Array.isArray(runs) ? runs : [];
    const scoped =
      scopedScheduleIds.length > 0
        ? (() => {
            const lookup = new Set(scopedScheduleIds);
            return source.filter((row) => {
              const runScheduleId = resolveRunScheduleId(row);
              return runScheduleId != null && lookup.has(`${runScheduleId}`);
            });
          })()
        : source;

    const statusScoped =
      statusFilter === 'history'
        ? scoped.filter((row) => !isRunOpen(row))
        : statusFilter === 'active'
          ? scoped.filter((row) => isRunOpen(row))
          : scoped;

    const filtered = statusScoped;

    return filtered.map((row) => ({ ...row, scheduleId: resolveRunScheduleId(row) }));
  }, [runs, scopedScheduleIds, statusFilter]);

  const handleRowClick = (row) => {
    const runScheduleId = resolveRunScheduleId(row);
    if (!runScheduleId) return;
    const schedule = scheduleLookup.get(`${runScheduleId}`) || null;
    if (schedule) setActiveEditSchedule(schedule);
    if (enableRunSelection && row?.id != null) {
      setActiveRunId?.(row.id);
    }
  };

  const scrollContainerStyle = useMemo(
    () =>
      useParentScroll
        ? { flex: 1, minHeight: 0 }
        : { flex: 1, minHeight: 0, overflow: 'auto', maxHeight: '100%' },
    [useParentScroll],
  );

  return (
    <div className="flex-grow-1 d-flex flex-column" style={{ minHeight: 0 }}>
      <div style={scrollContainerStyle}>
        <BaseTable
          columns={columns}
          rows={rows}
          onRowClick={handleRowClick}
          enableRowVirtualization={!useParentScroll}
          muiTableContainerProps={
            useParentScroll
              ? {
                  sx: {
                    maxHeight: 'none !important',
                    height: 'auto !important',
                    overflowY: 'visible !important',
                  },
                }
              : {}
          }
          customStyles={{
            cell: {
              borderBottom: '1px solid #56577A',
              color: 'white',
            },
            row: {
              '&:hover': { backgroundColor: '#2C373B' },
            },
          }}
        />
      </div>
    </div>
  );
};
